/**
 * The payee's Edit dialog (TMMAPP-0123 item 22): everything a person sets on a payee, in one place
 * with one Save (the page's "What members see" card and privacy section are gone).
 *
 * - The name, the kind and the notes (`PATCH /v1/me/payees/{id}`), with the classifier's kind
 *   suggestion and its reason in catalog words (TMMAPI-0157 D4).
 * - **Keep out of AI** (`aiExcluded`, the same PATCH), its help line, and the suggestion to keep it
 *   out when the classifier made one.
 * - **Shows to members as** (`alternateName`, MM-D-117; `null` clears it): everyone but the owner
 *   reads it in place of the payee. A `400` (a card number, IBAN, email or account-number shape)
 *   lands on the field.
 * - **The default category per virtual account** the caller owns or manages (MM-D-126; `PUT |
 *   DELETE …/category-defaults/{vaId}`): a hint the server applies below an explicit choice.
 *
 * Save calls the existing per-field endpoints in sequence, only for what changed. A failure is shown
 * at its field and keeps the dialog open; the steps already saved are not sent again. A refused
 * name, kind, notes or Keep out of AI lands at its own field (from `details.backend.fields`), and
 * focus moves to the first field in error (TMMAPP-0124 item 17).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { categoriesApi, type Category } from '@/api/categories';
import { ErrorCodes, isApiError } from '@/api/errors';
import { errorCopy } from '@/api/errorCopy';
import { backendFields } from '@/api/errorDetails';
import {
  payeesApi,
  type Payee,
  type PayeeCategoryDefault,
  type PayeeKind,
  type PayeePatchBody,
} from '@/api/payees';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { ownedVirtualAccountsFrom } from '@/features/accounts/useOwnedEnvelopes';
import { useMe } from '@/session/meHooks';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';
import { counterpartyReasonText } from './counterpartyReason';

/** The alternate name's server limit (1–120 characters, trimmed). */
export const ALTERNATE_NAME_MAX = 120;

const TITLE_ID = 'payee-edit-title';
const KINDS: readonly PayeeKind[] = ['VENDOR', 'PERSON', 'OTHER'];

interface EditValues {
  name: string;
  kind: PayeeKind;
  notes: string;
  aiExcluded: boolean;
  alternateName: string;
  /** Category id per virtual account id; null is no default. */
  defaults: Record<number, number | null>;
}

function valuesOf(payee: Payee, defaults: PayeeCategoryDefault[], vaIds: number[]): EditValues {
  return {
    name: payee.name,
    kind: payee.kind,
    notes: payee.notes ?? '',
    aiExcluded: payee.aiExcluded,
    alternateName: payee.alternateName ?? '',
    defaults: Object.fromEntries(
      vaIds.map((id) => [
        id,
        defaults.find((row) => row.virtualAccountId === id)?.categoryId ?? null,
      ]),
    ),
  };
}

interface FieldErrors {
  form?: string | undefined;
  name?: string | undefined;
  kind?: string | undefined;
  notes?: string | undefined;
  aiExcluded?: string | undefined;
  alternateName?: string | undefined;
  defaults?: Record<number, string> | undefined;
}

/** The PATCH's own fields, in the dialog's order: a refusal naming one lands there (item 17). */
const PATCH_FIELDS = ['name', 'kind', 'notes', 'aiExcluded'] as const;
type PatchField = (typeof PATCH_FIELDS)[number];
const FOCUS_ORDER = [...PATCH_FIELDS, 'alternateName'] as const;

export interface PayeeEditDialogProps {
  open: boolean;
  payee: Payee;
  categoryDefaults: PayeeCategoryDefault[];
  onClose: () => void;
  /** After a save (complete or partial): the page re-reads the payee. */
  onSaved: () => Promise<void>;
  onNotify: (message: string) => void;
}

/** Mounted per opening (the page renders it only while open), so it starts from the served values. */
export function PayeeEditDialog({
  open,
  payee,
  categoryDefaults,
  onClose,
  onSaved,
  onNotify,
}: PayeeEditDialogProps) {
  const { t } = useTranslation();
  const me = useMe();
  // The virtual accounts the caller owns or manages (the allocate dialog's destinations).
  const virtualAccounts = useMemo(
    () => ownedVirtualAccountsFrom(me.virtualAccounts, []),
    [me.virtualAccounts],
  );
  const vaIds = virtualAccounts.map((row) => row.id);
  // What the server holds: the opening values, moved on by each step that saves.
  const [baseline, setBaseline] = useState<EditValues>(() =>
    valuesOf(payee, categoryDefaults, vaIds),
  );
  const [values, setValues] = useState<EditValues>(baseline);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const fieldIds = {
    name: useId(),
    kind: useId(),
    notes: useId(),
    aiExcluded: useId(),
    alternateName: useId(),
  };
  // Focus moves to the first field in error once it shows (TMMAPP-0124 item 17).
  const [focusTick, setFocusTick] = useState(0);
  const firstError = FOCUS_ORDER.find((field) => errors[field] !== undefined);
  const focusId = firstError !== undefined ? fieldIds[firstError] : null;
  useEffect(() => {
    if (focusTick === 0 || focusId === null) return;
    document.getElementById(focusId)?.focus();
  }, [focusTick, focusId]);
  const [categories, setCategories] = useState<Record<number, Category[]>>({});
  const dirty = !sameValues(values, baseline);
  const guard = useDialogDirtyGuard(open && dirty, onClose, busy, 0, open);
  const reasonText = counterpartyReasonText(t, payee.suggestionReason);
  // Pattern 10: a form dialog is full screen below sm.
  const fullScreen = useDialogFullScreen();

  const vaKey = vaIds.join(',');
  useEffect(() => {
    if (vaKey.length === 0) return;
    let cancelled = false;
    void Promise.all(
      vaKey.split(',').map(async (raw) => {
        const id = Number(raw);
        try {
          return [id, (await categoriesApi.list(id)).filter((c) => c.archivedAt === null)] as const;
        } catch {
          return [id, [] as Category[]] as const;
        }
      }),
    ).then((pairs) => {
      if (!cancelled) setCategories(Object.fromEntries(pairs));
    });
    return () => {
      cancelled = true;
    };
  }, [vaKey]);

  const set = <K extends keyof EditValues>(key: K, value: EditValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  async function handleSave() {
    if (busy) return;
    const name = values.name.trim();
    if (name.length === 0) {
      setErrors({ name: t('payee.edit.nameRequired') });
      setFocusTick((n) => n + 1);
      return;
    }
    setBusy(true);
    setErrors({});
    let saved = baseline;
    let wrote = false;
    // A failure stays at its field; what already saved shows on the page under the dialog.
    const fail = (next: FieldErrors) => {
      setErrors(next);
      setFocusTick((n) => n + 1);
      if (wrote) void onSaved();
    };
    try {
      // 1. The payee itself: name, kind, notes, Keep out of AI.
      const body: PayeePatchBody = {};
      if (name !== saved.name) body.name = name;
      if (values.kind !== saved.kind) body.kind = values.kind;
      if (values.notes.trim() !== saved.notes.trim() && values.notes.trim().length > 0) {
        body.notes = values.notes.trim();
      }
      if (values.aiExcluded !== saved.aiExcluded) body.aiExcluded = values.aiExcluded;
      if (Object.keys(body).length > 0) {
        try {
          await payeesApi.patch(payee.id, body);
        } catch (caught) {
          // A refusal that names its fields lands at each (catalog words, never server prose).
          const named = Object.keys(backendFields(caught)).filter((field): field is PatchField =>
            (PATCH_FIELDS as readonly string[]).includes(field),
          );
          fail(
            named.length > 0
              ? Object.fromEntries(named.map((field) => [field, t(`payee.edit.refused.${field}`)]))
              : { form: errorCopy(t, caught, 'accounts.errorGeneric') },
          );
          return;
        }
        saved = {
          ...saved,
          name,
          kind: values.kind,
          notes: values.notes,
          aiExcluded: values.aiExcluded,
        };
        setBaseline(saved);
        wrote = true;
      }

      // 2. What members see: the alternate name.
      const alternate = values.alternateName.trim();
      if (alternate !== saved.alternateName.trim()) {
        try {
          await payeesApi.patch(payee.id, {
            alternateName: alternate.length > 0 ? alternate : null,
          });
        } catch (caught) {
          const invalid = isApiError(caught) && caught.code === ErrorCodes.VALIDATION_FAILED;
          fail({
            alternateName: invalid
              ? t('payee.membersName.invalid')
              : errorCopy(t, caught, 'accounts.errorGeneric'),
          });
          return;
        }
        saved = { ...saved, alternateName: alternate };
        setBaseline(saved);
        wrote = true;
      }

      // 3. The category defaults, one virtual account at a time.
      for (const id of vaIds) {
        const next = values.defaults[id] ?? null;
        if (next === (saved.defaults[id] ?? null)) continue;
        try {
          if (next === null) await payeesApi.clearCategoryDefault(payee.id, id);
          else await payeesApi.setCategoryDefault(payee.id, id, next);
        } catch {
          fail({ defaults: { [id]: t('payee.categoryDefault.failed') } });
          return;
        }
        saved = { ...saved, defaults: { ...saved.defaults, [id]: next } };
        setBaseline(saved);
        wrote = true;
      }

      onNotify(t('payee.edit.saved'));
      await onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      open={open}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
    >
      <DialogTitle id={TITLE_ID}>{t('common.edit')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {errors.form !== undefined && <Alert severity="error">{errors.form}</Alert>}
          <TextField
            id={fieldIds.name}
            label={t('payee.name')}
            value={values.name}
            onChange={(event) => {
              set('name', event.target.value);
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            error={errors.name !== undefined}
            helperText={errors.name}
            fullWidth
          />
          <KindField
            id={fieldIds.kind}
            value={values.kind}
            suggested={payee.suggestedKind}
            reason={reasonText}
            error={errors.kind ?? null}
            onChange={(kind) => {
              set('kind', kind);
              setErrors((prev) => ({ ...prev, kind: undefined }));
            }}
          />
          <TextField
            id={fieldIds.notes}
            label={t('payee.notes')}
            value={values.notes}
            onChange={(event) => {
              set('notes', event.target.value);
              setErrors((prev) => ({ ...prev, notes: undefined }));
            }}
            error={errors.notes !== undefined}
            helperText={errors.notes}
            fullWidth
            multiline
            minRows={2}
          />
          <Box>
            <FormControlLabel
              control={
                <Switch
                  id={fieldIds.aiExcluded}
                  checked={values.aiExcluded}
                  onChange={(_event, checked) => {
                    set('aiExcluded', checked);
                    setErrors((prev) => ({ ...prev, aiExcluded: undefined }));
                  }}
                  slotProps={{
                    input: {
                      'aria-invalid': errors.aiExcluded !== undefined || undefined,
                      'aria-describedby':
                        errors.aiExcluded !== undefined
                          ? `${fieldIds.aiExcluded}-error`
                          : undefined,
                    },
                  }}
                />
              }
              label={t('payee.edit.keepOutOfAi')}
            />
            {errors.aiExcluded !== undefined && (
              <FormHelperText error id={`${fieldIds.aiExcluded}-error`}>
                {errors.aiExcluded}
              </FormHelperText>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('payee.aiExcludedHelp')}
            </Typography>
            {payee.suggestedAiExcluded === true && !values.aiExcluded && reasonText !== null && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <span data-counterparty-reason="">{reasonText}</span>
              </Alert>
            )}
          </Box>
          <TextField
            id={fieldIds.alternateName}
            label={t('payee.membersName.label')}
            placeholder={payee.name}
            value={values.alternateName}
            onChange={(event) => {
              set('alternateName', event.target.value);
              setErrors((prev) => ({ ...prev, alternateName: undefined }));
            }}
            error={errors.alternateName !== undefined}
            helperText={errors.alternateName ?? t('payee.membersName.help')}
            slotProps={{ htmlInput: { maxLength: ALTERNATE_NAME_MAX } }}
            fullWidth
          />
          {virtualAccounts.map((virtualAccount) => (
            <DefaultCategoryRow
              key={virtualAccount.id}
              virtualAccountName={virtualAccount.name}
              categories={categories[virtualAccount.id] ?? []}
              value={values.defaults[virtualAccount.id] ?? null}
              error={errors.defaults?.[virtualAccount.id] ?? null}
              onChange={(categoryId) =>
                setValues((prev) => ({
                  ...prev,
                  defaults: { ...prev.defaults, [virtualAccount.id]: categoryId },
                }))
              }
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={guard.requestClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <SubmitButton loading={busy} onClick={() => void handleSave()}>
          {t('common.save')}
        </SubmitButton>
      </DialogActions>
    </GuardedDialog>
  );
}

function KindField({
  id,
  value,
  suggested,
  reason,
  error,
  onChange,
}: {
  id: string;
  value: PayeeKind;
  suggested: PayeeKind | null;
  reason: string | null;
  error: string | null;
  onChange: (kind: PayeeKind) => void;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  const errorId = useId();
  return (
    <Stack spacing={0.5}>
      <FormControl fullWidth error={error !== null}>
        <InputLabel id={labelId}>{t('payee.edit.kind')}</InputLabel>
        <Select
          id={id}
          labelId={labelId}
          label={t('payee.edit.kind')}
          value={value}
          onChange={(event) => onChange(event.target.value as PayeeKind)}
          {...(error !== null ? { 'aria-describedby': errorId } : {})}
        >
          {KINDS.map((kind) => (
            <MenuItem key={kind} value={kind}>
              {t(`payee.kind.${kind}`)}
            </MenuItem>
          ))}
        </Select>
        {error !== null && <FormHelperText id={errorId}>{error}</FormHelperText>}
      </FormControl>
      {suggested !== null && suggested !== value && (
        <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('payee.suggestedKind', { kind: t(`payee.kind.${suggested}`) })}
            {reason !== null && ` · ${reason}`}
          </Typography>
          <Button size="small" onClick={() => onChange(suggested)}>
            {t('payee.apply')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}

function DefaultCategoryRow({
  virtualAccountName,
  categories,
  value,
  error,
  onChange,
}: {
  virtualAccountName: string;
  categories: Category[];
  value: number | null;
  error: string | null;
  onChange: (categoryId: number | null) => void;
}) {
  const { t } = useTranslation();
  const labelId = useId();
  const errorId = useId();
  // A default whose category is no longer offered still shows; it can be cleared.
  const known = value === null || categories.some((category) => category.id === value);
  return (
    <Box>
      <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ flex: '1 1 280px' }} error={error !== null}>
          <InputLabel id={labelId}>
            {t('payee.categoryDefault.label', { virtualAccount: virtualAccountName })}
          </InputLabel>
          <Select<string>
            labelId={labelId}
            label={t('payee.categoryDefault.label', { virtualAccount: virtualAccountName })}
            value={value === null || !known ? '' : String(value)}
            onChange={(event) => {
              const next = event.target.value;
              if (next !== '') onChange(Number(next));
            }}
            {...(error !== null ? { 'aria-describedby': errorId } : {})}
          >
            {categories.map((category) => (
              <MenuItem key={category.id} value={String(category.id)}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button disabled={value === null} onClick={() => onChange(null)}>
          {t('payee.categoryDefault.clear')}
        </Button>
      </Stack>
      {error !== null && (
        <FormHelperText error id={errorId}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
}
